import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, AlertCircle, CheckCircle, Film, Info } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { PageSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubmitFilm() {
  const [formData, setFormData] = useState({
    // Contact Details
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    
    // Film Details
    film_title: '',
    genre: '',
    runtime_minutes: '',
    completion_year: '',
    country_of_origin: '',
    language: '',
    
    // Credits
    director: '',
    writer: '',
    producer: '',
    cast: '',
    
    // Synopsis & Description
    logline: '',
    synopsis: '',
    
    // Target Audience & Market
    target_audience: '',
    comparable_films: '',
    festival_history: '',
    awards: '',
    
    // Links
    screener_link: '',
    screener_password: '',
    trailer_link: '',
    website_link: '',
    imdb_link: '',
    
    // Distribution Status
    current_distribution: '',
    territories_available: '',
    
    // Additional
    additional_info: '',
    how_heard: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        runtime_minutes: formData.runtime_minutes ? parseInt(formData.runtime_minutes) : null,
        completion_year: formData.completion_year ? parseInt(formData.completion_year) : null,
      };
      
      await axios.post(`${API}/film-submission`, payload);
      setSuccess(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="submit-film-page" className="min-h-screen pt-20 bg-neutral-950">
        <section className="py-24 md:py-32">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-500/10 border border-green-500/30 p-12"
            >
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h1 className="font-display text-4xl text-white uppercase tracking-wider mb-4">SUBMISSION RECEIVED</h1>
              <p className="text-neutral-300 text-lg mb-6">
                Thank you for submitting your film. Our curatorial team will review your submission and watch your screener.
              </p>
              <p className="text-neutral-400 mb-8">
                We aim to respond within 2-3 weeks. If your film is selected for our catalogue, we'll be in touch to discuss next steps.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all"
              >
                RETURN HOME
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="submit-film-page" className="min-h-screen pt-20 bg-neutral-950">
      <PageSEO page="submit" />
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_filmmaker-profits/artifacts/96ur1eac_older%20gods%20thumnail%20fog%20monster.jpg"
            alt="Submit your film for UK distribution - LBR Distribution"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4">
              JOIN OUR CATALOGUE
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-6">
              SUBMIT YOUR FILM
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Complete the form below with as much detail as possible. We review every submission carefully.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 bg-orange-500/10 border-y border-orange-500/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-start gap-4">
            <Info className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-white font-semibold mb-2">Before You Submit</h3>
              <p className="text-neutral-300 text-sm leading-relaxed">
                We only consider <strong className="text-white">completed films and documentaries</strong>. 
                We do not accept scripts, treatments, or projects still in production. 
                Please ensure you have a screener link ready for our team to review.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-12">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-400">{error}</span>
              </div>
            )}

            {/* Contact Details Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">1</span>
                CONTACT DETAILS
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="contact_name" className="text-white">Your Name *</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    data-testid="input-contact-name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="Your full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email" className="text-white">Email Address *</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    data-testid="input-contact-email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone" className="text-white">Phone Number</Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    data-testid="input-contact-phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="+44 7123 456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-white">Production Company</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    data-testid="input-company-name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="Your company name (if applicable)"
                  />
                </div>
              </div>
            </div>

            {/* Film Details Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">2</span>
                FILM DETAILS
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="film_title" className="text-white">Film Title *</Label>
                  <Input
                    id="film_title"
                    name="film_title"
                    data-testid="input-film-title"
                    value={formData.film_title}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="The title of your film"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Genre *</Label>
                  <Select
                    value={formData.genre}
                    onValueChange={(value) => handleSelectChange('genre', value)}
                  >
                    <SelectTrigger 
                      data-testid="select-genre"
                      className="bg-neutral-900 border-white/10 text-white focus:border-orange-500"
                    >
                      <SelectValue placeholder="Select genre" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-white/10">
                      <SelectItem value="drama">Drama</SelectItem>
                      <SelectItem value="comedy">Comedy</SelectItem>
                      <SelectItem value="horror">Horror</SelectItem>
                      <SelectItem value="thriller">Thriller</SelectItem>
                      <SelectItem value="sci-fi">Sci-Fi</SelectItem>
                      <SelectItem value="documentary">Documentary</SelectItem>
                      <SelectItem value="action">Action</SelectItem>
                      <SelectItem value="romance">Romance</SelectItem>
                      <SelectItem value="animation">Animation</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="runtime_minutes" className="text-white">Runtime (minutes) *</Label>
                  <Input
                    id="runtime_minutes"
                    name="runtime_minutes"
                    type="number"
                    data-testid="input-runtime"
                    value={formData.runtime_minutes}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="e.g. 90"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="completion_year" className="text-white">Year Completed *</Label>
                  <Input
                    id="completion_year"
                    name="completion_year"
                    type="number"
                    data-testid="input-year"
                    value={formData.completion_year}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="e.g. 2024"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country_of_origin" className="text-white">Country of Origin</Label>
                  <Input
                    id="country_of_origin"
                    name="country_of_origin"
                    data-testid="input-country"
                    value={formData.country_of_origin}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="e.g. United Kingdom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language" className="text-white">Language</Label>
                  <Input
                    id="language"
                    name="language"
                    data-testid="input-language"
                    value={formData.language}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="e.g. English"
                  />
                </div>
              </div>
            </div>

            {/* Credits Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">3</span>
                CREDITS
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="director" className="text-white">Director *</Label>
                  <Input
                    id="director"
                    name="director"
                    data-testid="input-director"
                    value={formData.director}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="Director name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="writer" className="text-white">Writer</Label>
                  <Input
                    id="writer"
                    name="writer"
                    data-testid="input-writer"
                    value={formData.writer}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="Writer/Screenwriter name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="producer" className="text-white">Producer(s)</Label>
                  <Input
                    id="producer"
                    name="producer"
                    data-testid="input-producer"
                    value={formData.producer}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="Producer names (comma separated)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cast" className="text-white">Main Cast</Label>
                  <Input
                    id="cast"
                    name="cast"
                    data-testid="input-cast"
                    value={formData.cast}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="Lead actors (comma separated)"
                  />
                </div>
              </div>
            </div>

            {/* Synopsis Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">4</span>
                SYNOPSIS & DESCRIPTION
              </h2>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="logline" className="text-white">Logline *</Label>
                  <p className="text-neutral-500 text-xs mb-2">A one or two sentence summary of your film</p>
                  <Textarea
                    id="logline"
                    name="logline"
                    data-testid="input-logline"
                    value={formData.logline}
                    onChange={handleChange}
                    required
                    rows={2}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                    placeholder="A brief, compelling summary of your film in 1-2 sentences..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="synopsis" className="text-white">Full Synopsis *</Label>
                  <Textarea
                    id="synopsis"
                    name="synopsis"
                    data-testid="input-synopsis"
                    value={formData.synopsis}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                    placeholder="A detailed synopsis of your film (without spoilers if possible)..."
                  />
                </div>
              </div>
            </div>

            {/* Target Audience Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">5</span>
                TARGET AUDIENCE & MARKET
              </h2>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="target_audience" className="text-white">Target Audience *</Label>
                  <p className="text-neutral-500 text-xs mb-2">Who is this film for? Be as specific as possible about demographics, interests, and viewing habits.</p>
                  <Textarea
                    id="target_audience"
                    name="target_audience"
                    data-testid="input-target-audience"
                    value={formData.target_audience}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                    placeholder="Describe your ideal viewer: age range, interests, what films they typically watch, why they'd connect with your film..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="comparable_films" className="text-white">Comparable Films</Label>
                  <Input
                    id="comparable_films"
                    name="comparable_films"
                    data-testid="input-comparable"
                    value={formData.comparable_films}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="e.g. Hereditary meets The Witch"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="festival_history" className="text-white">Festival History</Label>
                    <Textarea
                      id="festival_history"
                      name="festival_history"
                      data-testid="input-festivals"
                      value={formData.festival_history}
                      onChange={handleChange}
                      rows={3}
                      className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                      placeholder="List any festivals where your film has screened..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="awards" className="text-white">Awards & Recognition</Label>
                    <Textarea
                      id="awards"
                      name="awards"
                      data-testid="input-awards"
                      value={formData.awards}
                      onChange={handleChange}
                      rows={3}
                      className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                      placeholder="List any awards or nominations..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Links Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">6</span>
                SCREENER & LINKS
              </h2>
              <div className="bg-orange-500/10 border border-orange-500/30 p-4 mb-6">
                <p className="text-neutral-300 text-sm">
                  <strong className="text-white">Important:</strong> We need to watch your film to consider it. Please provide a secure screener link (Vimeo, Google Drive, etc.) that will remain active for at least 30 days.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="screener_link" className="text-white">Screener Link *</Label>
                  <Input
                    id="screener_link"
                    name="screener_link"
                    data-testid="input-screener"
                    value={formData.screener_link}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="https://vimeo.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="screener_password" className="text-white">Screener Password</Label>
                  <Input
                    id="screener_password"
                    name="screener_password"
                    data-testid="input-password"
                    value={formData.screener_password}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="Password (if required)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trailer_link" className="text-white">Trailer Link</Label>
                  <Input
                    id="trailer_link"
                    name="trailer_link"
                    data-testid="input-trailer"
                    value={formData.trailer_link}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="https://youtube.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website_link" className="text-white">Film Website</Label>
                  <Input
                    id="website_link"
                    name="website_link"
                    data-testid="input-website"
                    value={formData.website_link}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="https://yourfilm.com"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="imdb_link" className="text-white">IMDb Page</Label>
                  <Input
                    id="imdb_link"
                    name="imdb_link"
                    data-testid="input-imdb"
                    value={formData.imdb_link}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="https://imdb.com/title/..."
                  />
                </div>
              </div>
            </div>

            {/* Distribution Status Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">7</span>
                DISTRIBUTION STATUS
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="current_distribution" className="text-white">Current Distribution</Label>
                  <Textarea
                    id="current_distribution"
                    name="current_distribution"
                    data-testid="input-current-dist"
                    value={formData.current_distribution}
                    onChange={handleChange}
                    rows={3}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                    placeholder="Is your film currently distributed anywhere? If so, where and in what territories?"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="territories_available" className="text-white">Territories Available</Label>
                  <Textarea
                    id="territories_available"
                    name="territories_available"
                    data-testid="input-territories"
                    value={formData.territories_available}
                    onChange={handleChange}
                    rows={3}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                    placeholder="Which territories are still available for distribution? (e.g. Worldwide, UK only, etc.)"
                  />
                </div>
              </div>
            </div>

            {/* Additional Info Section */}
            <div>
              <h2 className="font-display text-2xl tracking-wider text-white uppercase mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-orange-600 flex items-center justify-center text-sm">8</span>
                ADDITIONAL INFORMATION
              </h2>
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="additional_info" className="text-white">Anything Else?</Label>
                  <Textarea
                    id="additional_info"
                    name="additional_info"
                    data-testid="input-additional"
                    value={formData.additional_info}
                    onChange={handleChange}
                    rows={4}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500 resize-none"
                    placeholder="Is there anything else you'd like us to know about your film or your goals for distribution?"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="how_heard" className="text-white">How did you hear about us?</Label>
                  <Input
                    id="how_heard"
                    name="how_heard"
                    data-testid="input-how-heard"
                    value={formData.how_heard}
                    onChange={handleChange}
                    className="bg-neutral-900 border-white/10 text-white placeholder:text-neutral-500 focus:border-orange-500"
                    placeholder="e.g. Google, referral, festival, etc."
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-8 border-t border-white/10">
              <button
                type="submit"
                disabled={loading || !formData.genre}
                data-testid="submit-form-btn"
                className="w-full flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-500 disabled:bg-orange-600/50 disabled:cursor-not-allowed text-white font-sans font-bold py-5 px-8 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Film size={20} />
                    SUBMIT FOR CONSIDERATION
                  </>
                )}
              </button>
              <p className="text-neutral-500 text-sm text-center mt-4">
                By submitting, you confirm that you have the rights to distribute this film and that all information provided is accurate.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
